'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ─── OBSERVATION COMPLETE COSMOLOGY NOTE ─────────────────────────────────────
// Shown at the moment observation is complete — the Pen Hadith anchor

export function ObservationCompleteCosmologyNote({ visible, onDismiss }: { visible: boolean; onDismiss: () => void }) {
  const [expanded, setExpanded] = useState(false);

  if (!visible) return null;

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(6,13,18,0.98) 0%, rgba(10,20,30,0.98) 100%)',
      border: '1px solid rgba(201,168,76,0.3)',
      borderLeft: '3px solid rgba(201,168,76,0.6)',
      borderRadius: '0 8px 8px 0',
      padding: '20px 20px 16px',
      marginTop: 16,
      fontFamily: "'Courier New', monospace",
      animation: 'fadeInUp 0.5s ease',
    }}>
      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      {/* Pen glyph */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'rgba(201,168,76,0.08)',
          border: '1px solid rgba(201,168,76,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, fontSize: 14, color: '#c9a84c',
        }}>✦</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>
            RECOGNITION COMPLETE — COSMOLOGICAL NOTE
          </div>
          <p style={{
            fontFamily: "'Georgia', serif",
            fontStyle: 'italic',
            fontSize: 14,
            color: '#d4c5a9',
            lineHeight: 1.8,
            marginBottom: 0,
          }}>
            "This recognition was decreed. You recognized what the Pen wrote. Your observation adds to the communal tafsir of the Qadr."
          </p>
        </div>
      </div>

      {/* Expandable depth */}
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em',
          textTransform: 'uppercase', fontFamily: 'inherit', padding: '4px 0',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        <span style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▶</span>
        {expanded ? 'Collapse' : 'What does this mean?'}
      </button>

      {expanded && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            {
              label: 'The Pen — First Creation',
              text: 'The first thing Allah created was the Pen. It was commanded to write the Qadr — the Divine Decree of everything until the Last Hour. What you just observed was already written before creation existed.',
              color: '#c9a84c',
            },
            {
              label: 'You Recognized, Not Discovered',
              text: 'The Names you extracted were already operative in this encounter. The Sunnat Allah you traced was already in process. You did not create this knowledge — you uncovered what was decreed.',
              color: '#2d8a87',
            },
            {
              label: 'Communal Tafsir of the Qadr',
              text: 'Every observation submitted to the Archive is a verse in the community\'s collective reading of the Decree. The Archive grows not because new information enters creation, but because more caretakers are reading what was always written.',
              color: '#2d7a47',
            },
            {
              label: 'The Pens Have Been Lifted',
              text: '"The pens have been lifted and the pages have dried." — Hadith 19, 40 Hadith an-Nawawi. What was decreed has been decreed. Your recognition was placed here for you to find.',
              color: '#8a8ab8',
            },
          ].map(item => (
            <div key={item.label} style={{
              padding: '10px 12px',
              background: `${item.color}06`,
              border: `1px solid ${item.color}20`,
              borderRadius: 4,
            }}>
              <div style={{ fontSize: 8, color: item.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>{item.label}</div>
              <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7, margin: 0 }}>{item.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Dismiss */}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onDismiss}
          style={{
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.25)',
            color: '#c9a84c',
            fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', borderRadius: 3,
          }}
        >
          Alhamdulillah — Continue
        </button>
      </div>
    </div>
  );
}

// ─── ROOT ANALYSIS EXPANDER ───────────────────────────────────────────────────
// After Layer 3 Names extraction — expandable root analysis with toggle-selectable dimensions

interface RootMeaning {
  id: string;
  dimension: string;
  cluster: 'measurement' | 'decree' | 'intellect' | 'power';
  gardenFunction: string;
  active: boolean;
}

const QADR_ROOT_MEANINGS: RootMeaning[] = [
  { id: 'measure', dimension: 'To measure something', cluster: 'measurement', gardenFunction: 'IRL Lens — Taqdir measurements in creation', active: false },
  { id: 'manifest', dimension: 'To make manifest the measure of something', cluster: 'measurement', gardenFunction: 'Zahir Layer — making the measure visible', active: false },
  { id: 'distribute', dimension: 'To distribute or apportion (as though by measure)', cluster: 'measurement', gardenFunction: 'MRG Distribution — resource apportionment', active: false },
  { id: 'measuring-out', dimension: 'Measuring out, Decree', cluster: 'decree', gardenFunction: 'Qadr Framework — the decree of all things', active: false },
  { id: 'ordain', dimension: 'To ordain', cluster: 'decree', gardenFunction: 'Observation Archive — reading what was ordained', active: false },
  { id: 'decide', dimension: 'To decide a matter (as though measuring to fit)', cluster: 'decree', gardenFunction: 'Three Questions Engine — precision of identification', active: false },
  { id: 'thought', dimension: 'To exercise thought in preparing something', cluster: 'intellect', gardenFunction: 'Fikr Process — the Aql Tower of preparation', active: false },
  { id: 'reason', dimension: 'To make use of reason', cluster: 'intellect', gardenFunction: 'Aql Root — rational faculty in recognition', active: false },
  { id: 'power', dimension: 'To have the power, To accomplish', cluster: 'power', gardenFunction: 'Amanah Layer — capacity to act on recognition', active: false },
  { id: 'able', dimension: 'To be rendered able, To be able', cluster: 'power', gardenFunction: 'Voluntary Submission — the capacity to choose', active: false },
  { id: 'prevail', dimension: 'To prevail, To be empowered', cluster: 'power', gardenFunction: 'Khalifah Role — stewardship as empowerment', active: false },
  { id: 'control', dimension: 'To have control over', cluster: 'power', gardenFunction: 'Qualified by the letter: only Allah has ultimate control', active: false },
];

const CLUSTER_COLORS: Record<string, string> = {
  measurement: '#2d8a87',
  decree: '#c9a84c',
  intellect: '#8a8ab8',
  power: '#2d7a47',
};

const CLUSTER_LABELS: Record<string, string> = {
  measurement: 'Measurement',
  decree: 'Decree',
  intellect: 'Intellect',
  power: 'Power & Capacity',
};

export function RootAnalysisExpander({
  nameId = 'qadir',
  nameAr = 'الْقَادِر',
  nameEn = 'Al-Qadir',
  rootLetters = 'ق - د - ر',
  encounterContext = '',
}: {
  nameId?: string;
  nameAr?: string;
  nameEn?: string;
  rootLetters?: string;
  encounterContext?: string;
}) {
  const [open, setOpen] = useState(false);
  const [meanings, setMeanings] = useState<RootMeaning[]>(QADR_ROOT_MEANINGS);
  const [activeCluster, setActiveCluster] = useState<string | null>(null);

  const activeMeanings = meanings.filter(m => m.active);
  const clusters = ['measurement', 'decree', 'intellect', 'power'];

  function toggleMeaning(id: string) {
    setMeanings(prev => prev.map(m => m.id === id ? { ...m, active: !m.active } : m));
  }

  const displayMeanings = activeCluster
    ? meanings.filter(m => m.cluster === activeCluster)
    : meanings;

  return (
    <div style={{ marginTop: 12, fontFamily: "'Courier New', monospace" }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          background: open ? 'rgba(201,168,76,0.06)' : 'rgba(6,13,18,0.6)',
          border: `1px solid ${open ? 'rgba(201,168,76,0.3)' : 'rgba(61,54,39,0.3)'}`,
          borderRadius: 4,
          padding: '10px 14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: '#c9a84c', direction: 'rtl' }}>{nameAr}</span>
          <div>
            <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.12em', textTransform: 'uppercase' }}>Root Analysis — {nameEn}</div>
            <div style={{ fontSize: 8, color: '#7a6f5d', marginTop: 2 }}>{rootLetters} · {meanings.length} meaning dimensions</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {activeMeanings.length > 0 && (
            <span style={{ fontSize: 8, color: '#2d7a47', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.25)', borderRadius: 3, padding: '2px 6px' }}>
              {activeMeanings.length} active
            </span>
          )}
          <span style={{ color: '#c9a84c', fontSize: 12, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▼</span>
        </div>
      </button>

      {open && (
        <div style={{
          background: 'rgba(6,13,18,0.95)',
          border: '1px solid rgba(61,54,39,0.3)',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          padding: '16px',
        }}>
          {/* Intro */}
          <div style={{
            padding: '10px 12px',
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.1)',
            borderRadius: 4,
            marginBottom: 14,
            fontSize: 10,
            color: '#9a8f7d',
            lineHeight: 1.7,
          }}>
            The root {rootLetters} contains {meanings.length} meaning dimensions — not synonyms, but a spectrum of capacities radiating from a single root. Toggle which dimensions are <strong style={{ color: '#d4c5a9' }}>active in this specific encounter</strong>. The root is the seed; the meanings are the branches.
          </div>

          {/* Cluster filter */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            <button
              onClick={() => setActiveCluster(null)}
              style={{
                padding: '3px 10px', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase',
                background: !activeCluster ? 'rgba(201,168,76,0.12)' : 'transparent',
                border: `1px solid ${!activeCluster ? 'rgba(201,168,76,0.4)' : 'rgba(61,54,39,0.3)'}`,
                color: !activeCluster ? '#c9a84c' : '#7a6f5d',
                cursor: 'pointer', fontFamily: 'inherit', borderRadius: 3,
              }}
            >All</button>
            {clusters.map(c => (
              <button
                key={c}
                onClick={() => setActiveCluster(activeCluster === c ? null : c)}
                style={{
                  padding: '3px 10px', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase',
                  background: activeCluster === c ? `${CLUSTER_COLORS[c]}15` : 'transparent',
                  border: `1px solid ${activeCluster === c ? CLUSTER_COLORS[c] + '50' : 'rgba(61,54,39,0.3)'}`,
                  color: activeCluster === c ? CLUSTER_COLORS[c] : '#7a6f5d',
                  cursor: 'pointer', fontFamily: 'inherit', borderRadius: 3,
                }}
              >{CLUSTER_LABELS[c]}</button>
            ))}
          </div>

          {/* Meaning dimensions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {displayMeanings.map(m => (
              <button
                key={m.id}
                onClick={() => toggleMeaning(m.id)}
                style={{
                  background: m.active ? `${CLUSTER_COLORS[m.cluster]}10` : 'rgba(6,13,18,0.5)',
                  border: `1px solid ${m.active ? CLUSTER_COLORS[m.cluster] + '50' : 'rgba(61,54,39,0.25)'}`,
                  borderLeft: `3px solid ${m.active ? CLUSTER_COLORS[m.cluster] : 'rgba(61,54,39,0.2)'}`,
                  borderRadius: '0 4px 4px 0',
                  padding: '10px 12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: m.active ? '#d4c5a9' : '#9a8f7d', marginBottom: 3, fontWeight: m.active ? 600 : 400 }}>
                      {m.dimension}
                    </div>
                    <div style={{ fontSize: 9, color: m.active ? CLUSTER_COLORS[m.cluster] : '#5a5a5a', lineHeight: 1.5 }}>
                      ↳ {m.gardenFunction}
                    </div>
                  </div>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    background: m.active ? CLUSTER_COLORS[m.cluster] : 'transparent',
                    border: `1px solid ${m.active ? CLUSTER_COLORS[m.cluster] : 'rgba(61,54,39,0.4)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, color: m.active ? '#060d12' : '#5a5a5a',
                  }}>
                    {m.active ? '✓' : ''}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Active summary */}
          {activeMeanings.length > 0 && (
            <div style={{
              marginTop: 14,
              padding: '12px 14px',
              background: 'rgba(45,122,71,0.06)',
              border: '1px solid rgba(45,122,71,0.2)',
              borderRadius: 4,
            }}>
              <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                ✦ Active in this encounter ({activeMeanings.length} of {meanings.length} dimensions)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {activeMeanings.map(m => (
                  <span key={m.id} style={{
                    fontSize: 9, padding: '2px 8px',
                    background: `${CLUSTER_COLORS[m.cluster]}12`,
                    border: `1px solid ${CLUSTER_COLORS[m.cluster]}30`,
                    color: CLUSTER_COLORS[m.cluster],
                    borderRadius: 3,
                  }}>{m.dimension}</span>
                ))}
              </div>
              {encounterContext && (
                <p style={{ fontSize: 10, color: '#7a6f5d', marginTop: 8, lineHeight: 1.6, fontStyle: 'italic' }}>
                  In this encounter with {encounterContext}, these dimensions of {nameEn} are manifest.
                </p>
              )}
            </div>
          )}

          {/* Root Fission note */}
          <div style={{
            marginTop: 12,
            padding: '8px 10px',
            background: 'rgba(138,138,184,0.04)',
            border: '1px solid rgba(138,138,184,0.12)',
            borderRadius: 3,
            fontSize: 9,
            color: '#7a6f5d',
            lineHeight: 1.6,
          }}>
            <span style={{ color: '#8a8ab8' }}>Root Fission:</span> You extracted {nameEn} from this encounter. Now you've entered the root — {rootLetters} — and identified which of its {meanings.length} meaning dimensions are active here. Not all meanings manifest in every encounter. The root is the same; the expression is contextual.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ROOT MODULES ARCHITECTURE ────────────────────────────────────────────────
// ق-د-ر as seed whose 12 meanings map to 12 garden functions

export function RootModulesArchitecture() {
  const [planted, setPlanted] = useState(false);
  const [expandedBranch, setExpandedBranch] = useState<string | null>(null);

  const branches = [
    { id: 'measure', meaning: 'To measure something', module: 'IRL Lens', function: 'Taqdir measurements in creation — every attribute is a prescribed measure', color: '#2d8a87', icon: '◈' },
    { id: 'manifest', meaning: 'To make manifest the measure', module: 'Zahir Layer', function: 'The visible surface of the decree — what the lens first encounters', color: '#2d8a87', icon: '◎' },
    { id: 'distribute', meaning: 'To distribute or apportion', module: 'MRG', function: 'Resource distribution as Qadr — provision apportioned by measure', color: '#2d7a47', icon: '⬡' },
    { id: 'decree', meaning: 'Measuring out, Decree', module: 'Qadr Framework', function: 'The Archive as communal tafsir — reading what was decreed', color: '#c9a84c', icon: '✦' },
    { id: 'ordain', meaning: 'To ordain', module: 'Observation Chain', function: 'Each observation reads what was ordained before creation', color: '#c9a84c', icon: '◈' },
    { id: 'decide', meaning: 'To decide a matter (measuring to fit)', module: 'Three Questions', function: 'Precision of identification — Is it? What is it? When is it?', color: '#D4A82A', icon: '⌘' },
    { id: 'thought', meaning: 'To exercise thought in preparing', module: 'Fikr Process', function: 'The Aql Tower — reasoned preparation before action', color: '#8a8ab8', icon: '✦' },
    { id: 'reason', meaning: 'To make use of reason', module: 'Aql Root', function: 'The rational faculty — Aql as the bridge between seen and unseen', color: '#8a8ab8', icon: '◎' },
    { id: 'power', meaning: 'To have the power, To accomplish', module: 'Amanah Layer', function: 'Capacity to act on recognition — the trust made manifest', color: '#2d7a47', icon: '⬡' },
    { id: 'able', meaning: 'To be rendered able', module: 'Voluntary Submission', function: 'The capacity to choose — Islam as willed, not compelled', color: '#2d7a47', icon: '◎' },
    { id: 'prevail', meaning: 'To prevail, To be empowered', module: 'Khalifah Role', function: 'Stewardship as empowerment — the caretaker\'s authority is delegated', color: '#c9a84c', icon: '✦' },
    { id: 'control', meaning: 'To have control over', module: 'Tawhid Anchor', function: 'Qualified immediately: only Allah has ultimate control. The caretaker holds perceived agency.', color: '#c94a2a', icon: '◈' },
  ];

  return (
    <div style={{
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 8,
      padding: '20px',
      fontFamily: "'Courier New', monospace",
    }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>
          ✦ ROOT MODULES ARCHITECTURE
        </div>
        <div style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#c9a84c', direction: 'rtl', marginBottom: 6 }}>ق - د - ر</div>
        <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7, marginBottom: 12 }}>
          A single Arabic root contains an entire module taxonomy. The twelve meanings of Qaf-Dal-Ra map to twelve different functions of the garden. The root is the seed. The twelve meanings are the twelve branches that grow from it.
        </p>
        <p style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.6, fontStyle: 'italic' }}>
          "Plant the ق-د-ر root module and get access to everything that radiates from that root: measurement tools, decree recognition, distribution analysis, reasoned preparation, empowerment frameworks."
        </p>
      </div>

      {/* Plant button */}
      {!planted ? (
        <button
          onClick={() => setPlanted(true)}
          style={{
            width: '100%',
            padding: '12px',
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.3)',
            color: '#c9a84c',
            fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: 'inherit', borderRadius: 4,
            marginBottom: 16,
          }}
        >
          ⬡ Plant the ق-د-ر Root Module
        </button>
      ) : (
        <div style={{
          padding: '10px 12px',
          background: 'rgba(45,122,71,0.08)',
          border: '1px solid rgba(45,122,71,0.25)',
          borderRadius: 4,
          marginBottom: 16,
          fontSize: 9, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase',
        }}>
          ✓ Root planted — 12 branches growing
        </div>
      )}

      {/* Branches */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {branches.map((branch, i) => (
          <div key={branch.id}>
            <button
              onClick={() => setExpandedBranch(expandedBranch === branch.id ? null : branch.id)}
              style={{
                width: '100%',
                background: expandedBranch === branch.id ? `${branch.color}08` : 'transparent',
                border: `1px solid ${expandedBranch === branch.id ? branch.color + '30' : 'rgba(61,54,39,0.2)'}`,
                borderLeft: `2px solid ${planted ? branch.color + '60' : 'rgba(61,54,39,0.2)'}`,
                borderRadius: '0 4px 4px 0',
                padding: '8px 12px',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: 8, color: planted ? branch.color : '#5a5a5a', width: 14, textAlign: 'center' }}>{planted ? branch.icon : String(i + 1).padStart(2, '0')}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: planted ? '#d4c5a9' : '#7a6f5d' }}>{branch.meaning}</div>
                <div style={{ fontSize: 8, color: planted ? branch.color : '#4a4a4a', marginTop: 1 }}>→ {branch.module}</div>
              </div>
              <span style={{ color: '#5a5a5a', fontSize: 9 }}>{expandedBranch === branch.id ? '▲' : '▼'}</span>
            </button>
            {expandedBranch === branch.id && (
              <div style={{
                padding: '10px 14px 10px 28px',
                background: `${branch.color}05`,
                border: `1px solid ${branch.color}15`,
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
                fontSize: 10,
                color: '#9a8f7d',
                lineHeight: 1.7,
              }}>
                {branch.function}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 16,
        padding: '10px 12px',
        background: 'rgba(201,168,76,0.04)',
        border: '1px solid rgba(201,168,76,0.1)',
        borderRadius: 4,
        fontSize: 9,
        color: '#7a6f5d',
        lineHeight: 1.6,
      }}>
        This transforms the SL Glossary from a reference tool into a generative architecture. Each root planted produces a cluster of related modules. The root system is literally composed of Arabic roots.
      </div>
    </div>
  );
}

// ─── IRL LENS SELF-REFERENTIAL META-RECOGNITION ───────────────────────────────
// The lens turns on itself — "this tool is composed of Names"

export function IRLLensSelfReferential({ visible = true }: { visible?: boolean }) {
  const [open, setOpen] = useState(false);

  if (!visible) return null;

  const layers = [
    {
      name: 'Al-Baseer — الْبَصِير',
      nameEn: 'The All-Seeing',
      text: 'This lens exists because Al-Baseer granted you sight. The faculty of seeing is a gift — not a default. The capacity to perceive Ayat was given before the tool was built.',
      color: '#2d8a87',
    },
    {
      name: 'Sand and Glass — الْمَتَاع',
      nameEn: 'The Mata (Provision)',
      text: 'The screen you\'re reading this on is sand and glass — entrusted to you, reshaped into a tool. Silicon dioxide, refined, layered, illuminated. Every component is Mata: provision whose purpose is not the provision itself.',
      color: '#c9a84c',
    },
    {
      name: 'An-Nur — النُّور',
      nameEn: 'The Light',
      text: 'The light illuminating this screen is a provision from An-Nur. Without light, the lens is dark. The illumination that makes seeing possible is from The Light.',
      color: '#D4A82A',
    },
    {
      name: 'Al-Khaliq — الْخَالِق',
      nameEn: 'The Creator',
      text: 'The Names you\'re extracting from creation are also composing this tool. The lens is itself an Ayah — a sign pointing back to the One who created the capacity to see, the material to build with, and the light to see by.',
      color: '#2d7a47',
    },
  ];

  return (
    <div style={{
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(45,138,135,0.25)',
      borderRadius: 8,
      fontFamily: "'Courier New', monospace",
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          padding: '14px 18px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontFamily: 'inherit',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 18, color: '#2d8a87' }}>◈</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: '#2d8a87', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 3 }}>
            IRL LENS — META-RECOGNITION LAYER
          </div>
          <div style={{ fontSize: 12, color: '#d4c5a9' }}>The Lens Turns on Itself</div>
        </div>
        <span style={{ color: '#2d8a87', fontSize: 12, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▼</span>
      </button>

      {open && (
        <div style={{ padding: '0 18px 18px', borderTop: '1px solid rgba(45,138,135,0.15)' }}>
          <div style={{
            padding: '12px 14px',
            background: 'rgba(45,138,135,0.06)',
            border: '1px solid rgba(45,138,135,0.15)',
            borderRadius: 4,
            margin: '14px 0',
            fontFamily: "'Georgia', serif",
            fontStyle: 'italic',
            fontSize: 13,
            color: '#d4c5a9',
            lineHeight: 1.8,
          }}>
            "This lens is itself an Ayah."
          </div>

          <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7, marginBottom: 14 }}>
            The IRL Lens is the project's primary tool. But the tool itself is composed of Names. The Mata principle applied to the platform's own instruments: if every tool in the garden is Mata — a provision whose purpose is not the tool itself — then the IRL Lens must know that it, too, is Mata. Its purpose is not the lens. Its purpose is what the lens reveals.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {layers.map(layer => (
              <div key={layer.name} style={{
                padding: '12px 14px',
                background: `${layer.color}06`,
                border: `1px solid ${layer.color}20`,
                borderLeft: `3px solid ${layer.color}`,
                borderRadius: '0 4px 4px 0',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: layer.color, direction: 'rtl' }}>{layer.name.split(' — ')[1]}</span>
                  <div>
                    <div style={{ fontSize: 9, color: layer.color, letterSpacing: '.08em', textTransform: 'uppercase' }}>{layer.name.split(' — ')[0]}</div>
                    <div style={{ fontSize: 8, color: '#7a6f5d' }}>{layer.nameEn}</div>
                  </div>
                </div>
                <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7, margin: 0 }}>{layer.text}</p>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 14,
            padding: '10px 12px',
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.1)',
            borderRadius: 4,
            fontSize: 10,
            color: '#7a6f5d',
            lineHeight: 1.6,
          }}>
            <strong style={{ color: '#c9a84c' }}>The gold-plated screwdriver warning:</strong> The moment the caretaker begins treasuring the tool more than what the tool shows, the Mata principle applies. The lens is not the destination. What the lens reveals is the destination.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VOCATION LENS MODULE ─────────────────────────────────────────────────────
// 6 vocations with universal prompt "What would it look like if you were mindful of Allah in these efforts?"

interface VocationLens {
  id: string;
  role: string;
  arabicRole: string;
  icon: string;
  color: string;
  irlOrientation: string;
  universalPrompt: string;
  specificPrompts: string[];
  amanahDomain: string;
  mrgBridge: string;
  keysPath: string;
  connectionTo: string;
}

const VOCATION_LENSES: VocationLens[] = [
  {
    id: 'engineer',
    role: 'Engineer',
    arabicRole: 'المهندس',
    icon: '⚙',
    color: '#2d8a87',
    irlOrientation: 'The fission sequence becomes a systems decomposition. What are the inputs, processes, outputs, and feedback loops of this system? Which Names govern each stage?',
    universalPrompt: 'What would your engineering work look like if it were oriented toward remembrance of Allah, Al-Qadir — The All-Powerful who measures all things?',
    specificPrompts: [
      'What tools are you working on, and what tools do you utilize toward that purpose?',
      'What systems are you building? What Taqdir governs the measurements in your work?',
      'Which Names are you unknowingly serving? If you design a water filtration system, you are serving Ar-Razzaq and Al-Quddus whether you know it or not.',
      'What would it look like if you designed with the Mizan — the balance — as the primary constraint?',
    ],
    amanahDomain: 'Systems as Ayat — every engineered system is a reading of Sunnat Allah',
    mrgBridge: 'Infrastructure & Environment',
    keysPath: 'Key 03: Sunnat Allah → Key 05: Amanah',
    connectionTo: 'Teacher (your systems become curriculum), Scientist (your tools enable research)',
  },
  {
    id: 'teacher',
    role: 'Teacher',
    arabicRole: 'المعلم',
    icon: '◎',
    color: '#D4A82A',
    irlOrientation: 'The curriculum itself becomes the encounter. What Names are embedded in what you teach? What Sunnat Allah does your subject reveal?',
    universalPrompt: 'What would your curriculum look like if its purpose were oriented toward remembrance — toward helping students recognize the Names in what they study?',
    specificPrompts: [
      'What does your curriculum look like when its purpose is oriented toward remembrance?',
      'The Keys to Kulli Haal is itself a curriculum — but your existing curriculum, in your school, in your ward, is also a curriculum that can be seen through the Lens.',
      'What breakthrough knowledge motivates your teaching? Which Names does it reveal?',
      'What would it look like if every lesson ended with: "What does this tell us about the One who created this?"',
    ],
    amanahDomain: 'Knowledge as Amanah — the teacher holds the trust of the student\'s Fitra',
    mrgBridge: 'Education & Learning',
    keysPath: 'Key 01: Ayat → Key 02: Haal → Key 07: Yaqeen',
    connectionTo: 'Scientist (your students become researchers), Engineer (your curriculum uses their tools)',
  },
  {
    id: 'farmer',
    role: 'Farmer',
    arabicRole: 'الفلاح',
    icon: '⬡',
    color: '#2d7a47',
    irlOrientation: 'The fission sequence becomes a soil analysis. What Names are active in the growth cycle? What Sunnat Allah governs germination, growth, harvest, and composting?',
    universalPrompt: 'What would your farming work look like if every seed planted were an act of recognition — acknowledging Al-Muhyi, The Giver of Life, in every germination?',
    specificPrompts: [
      'Which kinds of plants are you planting? Where, how, why?',
      'What Ayat, Signs are you pondering while planting?',
      'The land that is pure comes forth its vegetation by permission of its Lord — Quran 7:58. What does this mean for how you tend the soil?',
      'What would it look like if your farm were a living tafsir of Quran 15:19-23?',
    ],
    amanahDomain: 'Soil as Amanah — the earth is a trust, not a resource',
    mrgBridge: 'Food & Nutrition · Environment & Ecology',
    keysPath: 'Key 04: Mizan → Key 05: Amanah',
    connectionTo: 'Native (your land stewardship connects to indigenous ecological knowledge), Returning Citizen (your harvest feeds community food programs)',
  },
  {
    id: 'returning-citizen',
    role: 'Returning Citizen',
    arabicRole: 'العائد',
    icon: '✦',
    color: '#c9a84c',
    irlOrientation: 'The fission sequence applied to community barriers becomes a map of where Islah — restoration — is most needed. The returning citizen sees the Mizan transgressed and knows where to restore it.',
    universalPrompt: 'What reforms and resource redistribution propositions do you have for existing community institutions? What are the core tenets of your reasoning? This is your Lens — the most powerful reform instrument the platform can produce.',
    specificPrompts: [
      'What reforms and resource redistribution propositions do you have for existing community institutions?',
      'What are the core tenets of your reasoning? Your lens is the most powerful reform instrument the platform can produce.',
      'You bring an understanding of what\'s broken that no other perspective has.',
      'The returning citizen doesn\'t just receive MRG resources — they reshape them.',
      'What would Mizan restoration look like in your ward, from your vantage point?',
    ],
    amanahDomain: 'Islah as Amanah — restoration is the trust carried by those who have seen the system from inside',
    mrgBridge: 'Justice & Advocacy · Housing & Shelter · Economic Empowerment',
    keysPath: 'Key 04: Mizan → Key 05: Amanah → Key 06: Islah',
    connectionTo: 'Law Student (your reform proposals become their thesis), Community Organizer (your knowledge feeds mutual aid networks)',
  },
  {
    id: 'custodian',
    role: 'Custodian',
    arabicRole: 'الحارس',
    icon: '◈',
    color: '#8a8ab8',
    irlOrientation: 'The custodian sees what others overlook. The fission sequence applied to the spaces you maintain reveals the Names active in the act of cleaning, preserving, and protecting.',
    universalPrompt: 'How would you approach the custodianship of endangered habitats — or the spaces you already tend — if you recognized that every act of cleaning is an act of recognizing Al-Quddus, The Pure?',
    specificPrompts: [
      'How would a janitor approach the custodianship of endangered habitats?',
      'What Ayat are present in the spaces you maintain?',
      'Al-Quddus — The Pure — is active in every act of cleaning. What does it mean to clean in recognition of this Name?',
      'What would it look like if custodianship were understood as the highest form of environmental stewardship?',
    ],
    amanahDomain: 'Purity as Amanah — the custodian holds the trust of the space\'s sanctity',
    mrgBridge: 'Environment & Ecology · Community & Connection',
    keysPath: 'Key 01: Ayat → Key 05: Amanah',
    connectionTo: 'Native (your custodianship of spaces connects to ecological stewardship), Engineer (your maintenance reveals the systems they built)',
  },
  {
    id: 'law-student',
    role: 'Law Student',
    arabicRole: 'طالب القانون',
    icon: '⌘',
    color: '#c94a2a',
    irlOrientation: 'The fission sequence applied to legislation reveals the Names that law either honors or transgresses. Al-Adl — The Just — is the criterion by which every law is measured.',
    universalPrompt: 'How would you draft relevant legislation to address the concerns you\'ve identified in your ward? What class would this project be for? What funding structure is sustaining the university hosting these classes?',
    specificPrompts: [
      'How would a law student draft relevant legislation to address these concerns as a thesis project?',
      'What class would this project be for? What other class or course routes are available?',
      'What funding structure is sustaining the university hosting these classes and trade workshops?',
      'What socioeconomic systems underpin this framework?',
      'Al-Adl — The Just — is the criterion. What would legislation look like if Al-Adl were the primary standard?',
    ],
    amanahDomain: 'Justice as Amanah — the law student holds the trust of the community\'s rights',
    mrgBridge: 'Justice & Advocacy · Education & Learning',
    keysPath: 'Key 04: Mizan → Key 05: Amanah → Key 06: Islah',
    connectionTo: 'Returning Citizen (their reform proposals become your thesis), Community Organizer (your legislation enables their networks)',
  },
];

export function VocationLensModule() {
  const [activeVocation, setActiveVocation] = useState<string | null>(null);
  const [savedVocation, setSavedVocation] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('haal_vocation_lens');
      if (saved) setSavedVocation(saved);
    }
  }, []);

  function selectVocation(id: string) {
    setActiveVocation(activeVocation === id ? null : id);
  }

  function saveVocation(id: string) {
    setSavedVocation(id);
    if (typeof window !== 'undefined') localStorage.setItem('haal_vocation_lens', id);
  }

  const activeVoc = VOCATION_LENSES.find(v => v.id === activeVocation);

  return (
    <div style={{
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 8,
      padding: '20px',
      fontFamily: "'Courier New', monospace",
    }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>
          ◈ VOCATION LENS MODULE
        </div>
        <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7, marginBottom: 8 }}>
          The same IRL Lens methodology, filtered through your specific work. Each vocation is a different angle on the same recognition. Select your vocational lens to see role-specific prompts.
        </p>
        <div style={{
          padding: '8px 10px',
          background: 'rgba(201,168,76,0.04)',
          border: '1px solid rgba(201,168,76,0.12)',
          borderRadius: 3,
          fontSize: 10,
          color: '#c9a84c',
          fontStyle: 'italic',
          lineHeight: 1.6,
        }}>
          Universal Prompt: "Fisabilillah — in the way of Allah, you are entrusted with something that is running out, and you're entrusted with the means to do something in that period. What defines the time you're entrusted with, and what would it look like if you were mindful of Allah in those efforts?"
        </div>
      </div>

      {/* Vocation grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
        {VOCATION_LENSES.map(voc => (
          <button
            key={voc.id}
            onClick={() => selectVocation(voc.id)}
            style={{
              background: activeVocation === voc.id ? `${voc.color}12` : 'rgba(6,13,18,0.6)',
              border: `1px solid ${activeVocation === voc.id ? voc.color + '50' : 'rgba(61,54,39,0.3)'}`,
              borderTop: `3px solid ${savedVocation === voc.id ? voc.color : activeVocation === voc.id ? voc.color + '80' : 'rgba(61,54,39,0.2)'}`,
              borderRadius: 4,
              padding: '12px 10px',
              cursor: 'pointer',
              textAlign: 'center',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
          >
            <div style={{ fontSize: 18, color: voc.color, marginBottom: 4 }}>{voc.icon}</div>
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: `${voc.color}80`, direction: 'rtl', marginBottom: 3 }}>{voc.arabicRole}</div>
            <div style={{ fontSize: 9, color: activeVocation === voc.id ? '#d4c5a9' : '#9a8f7d', letterSpacing: '.06em', textTransform: 'uppercase' }}>{voc.role}</div>
            {savedVocation === voc.id && (
              <div style={{ fontSize: 7, color: voc.color, marginTop: 3, letterSpacing: '.08em' }}>ACTIVE LENS</div>
            )}
          </button>
        ))}
      </div>

      {/* Active vocation detail */}
      {activeVoc && (
        <div style={{
          background: `${activeVoc.color}06`,
          border: `1px solid ${activeVoc.color}25`,
          borderRadius: 6,
          padding: '16px',
          animation: 'fadeIn 0.2s ease',
        }}>
          <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 22, color: activeVoc.color }}>{activeVoc.icon}</span>
            <div>
              <div style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: activeVoc.color, direction: 'rtl' }}>{activeVoc.arabicRole}</div>
              <div style={{ fontSize: 11, color: '#d4c5a9', fontWeight: 600 }}>{activeVoc.role} Lens</div>
            </div>
          </div>

          {/* Universal prompt */}
          <div style={{
            padding: '10px 12px',
            background: 'rgba(201,168,76,0.06)',
            border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 4,
            marginBottom: 12,
            fontFamily: "'Georgia', serif",
            fontStyle: 'italic',
            fontSize: 12,
            color: '#d4c5a9',
            lineHeight: 1.8,
          }}>
            {activeVoc.universalPrompt}
          </div>

          {/* IRL Orientation */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 8, color: activeVoc.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>IRL Lens Orientation</div>
            <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7, margin: 0 }}>{activeVoc.irlOrientation}</p>
          </div>

          {/* Specific prompts */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 8, color: activeVoc.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Role-Specific Prompts</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {activeVoc.specificPrompts.map((prompt, i) => (
                <div key={i} style={{
                  padding: '8px 10px',
                  background: 'rgba(6,13,18,0.5)',
                  border: `1px solid ${activeVoc.color}15`,
                  borderLeft: `2px solid ${activeVoc.color}40`,
                  borderRadius: '0 3px 3px 0',
                  fontSize: 10,
                  color: '#9a8f7d',
                  lineHeight: 1.6,
                }}>
                  {prompt}
                </div>
              ))}
            </div>
          </div>

          {/* Amanah + MRG + Keys */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div style={{ padding: '8px 10px', background: 'rgba(6,13,18,0.5)', border: `1px solid ${activeVoc.color}15`, borderRadius: 4 }}>
              <div style={{ fontSize: 7, color: activeVoc.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Amanah Domain</div>
              <p style={{ fontSize: 9, color: '#9a8f7d', lineHeight: 1.5, margin: 0 }}>{activeVoc.amanahDomain}</p>
            </div>
            <div style={{ padding: '8px 10px', background: 'rgba(6,13,18,0.5)', border: `1px solid ${activeVoc.color}15`, borderRadius: 4 }}>
              <div style={{ fontSize: 7, color: activeVoc.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Keys Path</div>
              <p style={{ fontSize: 9, color: '#9a8f7d', lineHeight: 1.5, margin: 0 }}>{activeVoc.keysPath}</p>
            </div>
          </div>

          {/* Vocational connection */}
          <div style={{
            padding: '8px 10px',
            background: 'rgba(45,122,71,0.06)',
            border: '1px solid rgba(45,122,71,0.15)',
            borderRadius: 4,
            marginBottom: 12,
          }}>
            <div style={{ fontSize: 7, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Vocational Connections</div>
            <p style={{ fontSize: 9, color: '#9a8f7d', lineHeight: 1.5, margin: 0 }}>{activeVoc.connectionTo}</p>
          </div>

          {/* MRG Bridge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase' }}>MRG Bridge:</div>
            <div style={{ fontSize: 9, color: '#2d7a47', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 3, padding: '2px 8px' }}>{activeVoc.mrgBridge}</div>
          </div>

          {/* Save + MRG link */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => saveVocation(activeVoc.id)}
              style={{
                flex: 1,
                padding: '8px',
                background: savedVocation === activeVoc.id ? `${activeVoc.color}15` : 'rgba(6,13,18,0.6)',
                border: `1px solid ${savedVocation === activeVoc.id ? activeVoc.color + '50' : 'rgba(61,54,39,0.3)'}`,
                color: savedVocation === activeVoc.id ? activeVoc.color : '#7a6f5d',
                fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: 'inherit', borderRadius: 4,
              }}
            >
              {savedVocation === activeVoc.id ? '✓ Active Lens' : 'Set as Active Lens'}
            </button>
            <Link
              href="/muslim-resource-guide"
              style={{
                padding: '8px 14px',
                background: 'rgba(45,122,71,0.08)',
                border: '1px solid rgba(45,122,71,0.25)',
                color: '#2d7a47',
                fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                textDecoration: 'none', borderRadius: 4, display: 'flex', alignItems: 'center',
              }}
            >
              → MRG
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VOCATIONAL CONNECTIONS MAP ───────────────────────────────────────────────
// Wiring between gardens — engineer feeds teacher, returning citizen feeds MRG

export function VocationalConnectionsMap() {
  const [activeConnection, setActiveConnection] = useState<string | null>(null);

  const connections = [
    {
      id: 'eng-teach',
      from: 'Engineer',
      to: 'Teacher',
      fromColor: '#2d8a87',
      toColor: '#D4A82A',
      description: 'The engineer\'s tools become the teacher\'s curriculum. Systems built by engineers are studied by students. The engineer\'s observations feed the teacher\'s lesson plans.',
      example: 'An engineer documents the water filtration system in their ward → the teacher builds a curriculum around water as Ayah → students learn both ecology and Names.',
    },
    {
      id: 'teach-sci',
      from: 'Teacher',
      to: 'Scientist',
      fromColor: '#D4A82A',
      toColor: '#8a8ab8',
      description: 'The teacher\'s students become the scientists. The curriculum produces the researchers. The teacher\'s framing of knowledge shapes what questions the scientist asks.',
      example: 'A teacher frames photosynthesis as Sunnat Allah → a student becomes a botanist asking "what Names are active in this process?" → research oriented toward recognition.',
    },
    {
      id: 'sci-farm',
      from: 'Scientist',
      to: 'Farmer',
      fromColor: '#8a8ab8',
      toColor: '#2d7a47',
      description: 'The scientist\'s research informs the farmer\'s practice. Soil science, mycorrhizal networks, seed genetics — all feed the farmer\'s understanding of what they tend.',
      example: 'A scientist documents mycorrhizal networks as Al-Jami in action → a farmer applies this understanding to companion planting → the garden becomes a living tafsir.',
    },
    {
      id: 'return-mrg',
      from: 'Returning Citizen',
      to: 'MRG',
      fromColor: '#c9a84c',
      toColor: '#2d7a47',
      description: 'The returning citizen\'s reform proposals feed the MRG\'s Justice-Involved resources. They don\'t just receive resources — they reshape them. Their lens is the most powerful reform instrument.',
      example: 'A returning citizen documents gaps in reentry housing → submits reform proposals to MRG → the MRG Justice-Involved section is updated with their analysis.',
    },
    {
      id: 'return-law',
      from: 'Returning Citizen',
      to: 'Law Student',
      fromColor: '#c9a84c',
      toColor: '#c94a2a',
      description: 'The returning citizen\'s lived experience becomes the law student\'s thesis material. The law student drafts legislation to address the concerns the returning citizen identified.',
      example: 'A returning citizen identifies a gap in reentry legislation → a law student drafts a reform bill as their thesis → the funding structure sustaining the university hosts the class.',
    },
    {
      id: 'custodian-native',
      from: 'Custodian',
      to: 'Native',
      fromColor: '#8a8ab8',
      toColor: '#2d7a47',
      description: 'The custodian\'s daily maintenance of spaces connects to the native\'s ecological stewardship. Both tend what others overlook. Both recognize the Names in the act of preservation.',
      example: 'A custodian documents the ecological conditions of the spaces they maintain → a native connects this to indigenous land stewardship practices → a shared recognition emerges.',
    },
  ];

  const activeConn = connections.find(c => c.id === activeConnection);

  return (
    <div style={{
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(45,122,71,0.2)',
      borderRadius: 8,
      padding: '20px',
      fontFamily: "'Courier New', monospace",
    }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>
          ⬡ VOCATIONAL CONNECTIONS — MYCORRHIZAL NETWORK
        </div>
        <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7 }}>
          The roles are a system, not a list. The engineer's work feeds the teacher's curriculum. The teacher's students become the scientists. The returning citizen's reforms feed the MRG. The mycorrhizal network between gardens is composed of these vocational connections.
        </p>
      </div>

      {/* Connection list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        {connections.map(conn => (
          <button
            key={conn.id}
            onClick={() => setActiveConnection(activeConnection === conn.id ? null : conn.id)}
            style={{
              background: activeConnection === conn.id ? 'rgba(45,122,71,0.08)' : 'transparent',
              border: `1px solid ${activeConnection === conn.id ? 'rgba(45,122,71,0.3)' : 'rgba(61,54,39,0.25)'}`,
              borderRadius: 4,
              padding: '10px 12px',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span style={{ fontSize: 9, color: conn.fromColor, background: `${conn.fromColor}10`, border: `1px solid ${conn.fromColor}25`, borderRadius: 3, padding: '2px 6px', flexShrink: 0 }}>{conn.from}</span>
            <span style={{ color: '#5a5a5a', fontSize: 10 }}>→</span>
            <span style={{ fontSize: 9, color: conn.toColor, background: `${conn.toColor}10`, border: `1px solid ${conn.toColor}25`, borderRadius: 3, padding: '2px 6px', flexShrink: 0 }}>{conn.to}</span>
            <span style={{ flex: 1, fontSize: 9, color: '#7a6f5d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conn.description.split('.')[0]}</span>
            <span style={{ color: '#5a5a5a', fontSize: 9, flexShrink: 0 }}>{activeConnection === conn.id ? '▲' : '▼'}</span>
          </button>
        ))}
      </div>

      {/* Active connection detail */}
      {activeConn && (
        <div style={{
          padding: '14px',
          background: 'rgba(45,122,71,0.06)',
          border: '1px solid rgba(45,122,71,0.2)',
          borderRadius: 6,
          animation: 'fadeIn 0.2s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 10, color: activeConn.fromColor, background: `${activeConn.fromColor}12`, border: `1px solid ${activeConn.fromColor}30`, borderRadius: 3, padding: '3px 8px' }}>{activeConn.from}</span>
            <span style={{ color: '#2d7a47', fontSize: 12 }}>⟶</span>
            <span style={{ fontSize: 10, color: activeConn.toColor, background: `${activeConn.toColor}12`, border: `1px solid ${activeConn.toColor}30`, borderRadius: 3, padding: '3px 8px' }}>{activeConn.to}</span>
          </div>
          <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7, marginBottom: 10 }}>{activeConn.description}</p>
          <div style={{ padding: '8px 10px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 3 }}>
            <div style={{ fontSize: 7, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Example Flow</div>
            <p style={{ fontSize: 10, color: '#9a8f7d', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>{activeConn.example}</p>
          </div>
        </div>
      )}

      {/* Fisabilillah closing */}
      <div style={{
        marginTop: 16,
        padding: '12px 14px',
        background: 'rgba(201,168,76,0.04)',
        border: '1px solid rgba(201,168,76,0.12)',
        borderRadius: 4,
        fontFamily: "'Georgia', serif",
        fontStyle: 'italic',
        fontSize: 12,
        color: '#d4c5a9',
        lineHeight: 1.8,
      }}>
        "The funding structure that sustains the university hosts the classes that produce the law student who drafts the legislation that addresses the concerns that the janitor identified." — The roles are a system. The garden models this.
      </div>
    </div>
  );
}

// ─── ROLE SELECTOR WITH LINEAGE ───────────────────────────────────────────────
// Role selector alongside lineage showing which vocational lens is active

export function RoleSelectorWithLineage({
  currentRole,
  currentLineage,
  onRoleChange,
}: {
  currentRole: string;
  currentLineage: string | null;
  onRoleChange: (role: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const LINEAGE_COLORS: Record<string, string> = {
    'forest-keeper': '#2d7a47',
    'water-guardian': '#2d8a87',
    'edge-walker': '#c9a84c',
    'restoration-warrior': '#c94a2a',
  };

  const lineageColor = currentLineage ? (LINEAGE_COLORS[currentLineage] || '#c9a84c') : '#c9a84c';

  const activeVoc = VOCATION_LENSES.find(v => v.id === currentRole);

  return (
    <div style={{ fontFamily: "'Courier New', monospace" }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          background: 'rgba(6,13,18,0.8)',
          border: `1px solid ${lineageColor}30`,
          borderLeft: `3px solid ${lineageColor}`,
          borderRadius: '0 6px 6px 0',
          padding: '10px 14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontFamily: 'inherit',
          textAlign: 'left',
        }}
      >
        {/* Lineage indicator */}
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: lineageColor,
          flexShrink: 0,
        }} />

        {/* Active vocation */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 }}>
            {currentLineage ? `${currentLineage.replace('-', ' ')} lineage` : 'No lineage set'} · Vocational Lens
          </div>
          <div style={{ fontSize: 11, color: activeVoc ? activeVoc.color : '#9a8f7d' }}>
            {activeVoc ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{activeVoc.icon}</span>
                <span>{activeVoc.role}</span>
                <span style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${activeVoc.color}60`, direction: 'rtl' }}>{activeVoc.arabicRole}</span>
              </span>
            ) : 'Select vocational lens'}
          </div>
        </div>

        <span style={{ color: '#7a6f5d', fontSize: 10, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▼</span>
      </button>

      {open && (
        <div style={{
          background: 'rgba(6,13,18,0.97)',
          border: `1px solid ${lineageColor}20`,
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          padding: '12px',
        }}>
          <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
            Select Vocational Lens — same methodology, different application
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {VOCATION_LENSES.map(voc => (
              <button
                key={voc.id}
                onClick={() => { onRoleChange(voc.id); setOpen(false); }}
                style={{
                  background: currentRole === voc.id ? `${voc.color}12` : 'transparent',
                  border: `1px solid ${currentRole === voc.id ? voc.color + '40' : 'rgba(61,54,39,0.25)'}`,
                  borderRadius: 4,
                  padding: '8px 6px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontSize: 14, color: voc.color, marginBottom: 3 }}>{voc.icon}</div>
                <div style={{ fontSize: 8, color: currentRole === voc.id ? '#d4c5a9' : '#7a6f5d', letterSpacing: '.04em', textTransform: 'uppercase' }}>{voc.role}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MODULE TILE WITH ROLE-SPECIFIC PROMPT ────────────────────────────────────
// Module tiles display role-specific prompt variants

interface ModuleTileProps {
  moduleId: string;
  moduleLabel: string;
  moduleColor: string;
  href: string;
  activeRole?: string;
}

const ROLE_MODULE_PROMPTS: Record<string, Record<string, string>> = {
  'engineer': {
    'irl-lens': 'Apply systems decomposition: what are the inputs, processes, outputs of this encounter?',
    'keys': 'Which Keys govern the systems you build? Map Key 03 (Sunnat Allah) to your engineering processes.',
    'mrg': 'What infrastructure gaps in your ward need engineering solutions? Document them here.',
    'names': 'Which Names are active in the systems you design? Al-Musawwir fashions form; Al-Qadir measures.',
    'ayat': 'Every engineered system is a reading of Sunnat Allah. What systems are you observing?',
  },
  'farmer': {
    'irl-lens': 'Apply soil analysis: what Names are active in the growth cycle you\'re observing?',
    'keys': 'Key 04 (Mizan) is the farmer\'s primary key. What balance are you maintaining in your soil?',
    'mrg': 'What food resources are available in your ward? What gaps exist in the food system?',
    'names': 'Al-Muhyi (The Life-Giver) and Al-Mumit (The Death-Bringer) are active in every harvest.',
    'ayat': 'The land that is pure comes forth its vegetation by permission of its Lord — Quran 7:58.',
  },
  'returning-citizen': {
    'irl-lens': 'Apply Mizan analysis: where is the balance transgressed in your ward? Map the restoration needed.',
    'keys': 'Key 06 (Islah) is your primary key. Restoration is the trust carried by those who have seen the system from inside.',
    'mrg': 'You don\'t just receive resources — you reshape them. What reforms would you propose to the MRG?',
    'names': 'Al-Adl (The Just) is the criterion. What would justice look like in your ward?',
    'ayat': 'The signs of Mizan transgressed are as visible as the signs of Mizan maintained. Document both.',
  },
  'teacher': {
    'irl-lens': 'Apply curriculum analysis: what Names are embedded in what you teach? What Sunnat Allah does your subject reveal?',
    'keys': 'The Keys to Kulli Haal is itself a curriculum. How does your existing curriculum connect to these Keys?',
    'mrg': 'What educational resources are available in your ward? What gaps exist in the learning ecosystem?',
    'names': 'Al-Alim (The All-Knowing) is the source of all knowledge. What does it mean to teach in recognition of this?',
    'ayat': 'Every subject is a door to Ayat. What Ayat does your subject open?',
  },
  'custodian': {
    'irl-lens': 'Apply preservation analysis: what Names are active in the act of cleaning and maintaining?',
    'keys': 'Key 01 (Ayat) is everywhere — including in the spaces you maintain. What Ayat are present in your ward?',
    'mrg': 'What environmental conditions in your ward need custodial attention? Document them here.',
    'names': 'Al-Quddus (The Pure) is active in every act of cleaning. What does it mean to clean in recognition of this Name?',
    'ayat': 'The custodian sees what others overlook. What Ayat are present in the spaces you tend?',
  },
  'law-student': {
    'irl-lens': 'Apply justice analysis: what Names does this law honor or transgress? Al-Adl is the criterion.',
    'keys': 'Key 04 (Mizan) and Key 06 (Islah) are the law student\'s primary keys. What legislation restores balance?',
    'mrg': 'What legal resources are available in your ward? What legislation would address the gaps you\'ve identified?',
    'names': 'Al-Adl (The Just) and Al-Hakam (The Judge) are the Names that govern legislation.',
    'ayat': 'The signs of injustice are Ayat of Mizan transgressed. Document them as evidence for reform.',
  },
};

export function ModuleTileWithRolePrompt({ moduleId, moduleLabel, moduleColor, href, activeRole = 'explorer' }: ModuleTileProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const rolePrompts = ROLE_MODULE_PROMPTS[activeRole];
  const prompt = rolePrompts?.[moduleId];

  return (
    <div style={{
      background: 'rgba(6,13,18,0.8)',
      border: `1px solid ${moduleColor}20`,
      borderRadius: 6,
      overflow: 'hidden',
      fontFamily: "'Courier New', monospace",
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: moduleColor, flexShrink: 0,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: '#d4c5a9', fontWeight: 600 }}>{moduleLabel}</div>
          {prompt && (
            <div style={{ fontSize: 8, color: `${moduleColor}80`, marginTop: 1 }}>
              {activeRole.replace('-', ' ')} lens active
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {prompt && (
            <button
              onClick={() => setShowPrompt(v => !v)}
              style={{
                padding: '3px 8px', fontSize: 7, letterSpacing: '.08em', textTransform: 'uppercase',
                background: showPrompt ? `${moduleColor}12` : 'transparent',
                border: `1px solid ${showPrompt ? moduleColor + '40' : 'rgba(61,54,39,0.3)'}`,
                color: showPrompt ? moduleColor : '#7a6f5d',
                cursor: 'pointer', fontFamily: 'inherit', borderRadius: 3,
              }}
            >
              {showPrompt ? '▲ Prompt' : '▼ Prompt'}
            </button>
          )}
          <Link
            href={href}
            style={{
              padding: '3px 10px', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase',
              background: `${moduleColor}10`,
              border: `1px solid ${moduleColor}30`,
              color: moduleColor,
              textDecoration: 'none', borderRadius: 3,
            }}
          >
            Open →
          </Link>
        </div>
      </div>

      {showPrompt && prompt && (
        <div style={{
          padding: '10px 12px',
          background: `${moduleColor}06`,
          borderTop: `1px solid ${moduleColor}15`,
          fontSize: 10,
          color: '#9a8f7d',
          lineHeight: 1.7,
          fontStyle: 'italic',
        }}>
          {prompt}
        </div>
      )}
    </div>
  );
}
